import type { TransactionCurrency } from '@contracts/payment/Currency';
import type { TransactionResult } from '@contracts/payment/TransactionResult';
import { PaymentClient } from '@src/orders/services/PaymentClient';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

interface Props {
  onSuccess: (preauthorizedAmount: number) => void;
  onError: (message: string) => void;
  orderId: string;
  clientId: string;
}

export const usePreauthorize = ({
  onSuccess,
  orderId,
  onError,
  clientId,
}: Props) => {
  const { t } = useTranslation();

  const onUnknownError = () => onError(t('preauthorization.error'));
  const onPreauthFailed = ({
    status,
    errorReason,
    errorCode,
  }: TransactionResult) =>
    onError(
      t('preauthorization.preauthFailed', {
        status,
        description: errorReason,
        errorCode,
      }),
    );

  const { mutate: preauthorize, isPending } = useMutation({
    mutationFn: async (props: {
      creditCardId: string;
      amount: number;
      currency: TransactionCurrency;
    }) => {
      const transactionResult =
        await PaymentClient.preauthorizeWithExistingCard({
          ...props,
          orderId,
          clientId,
        });
      return { transactionResult, amount: props.amount };
    },
    onSuccess: ({ transactionResult, amount }) => {
      if (transactionResult.status === 'SUCCESS') {
        onSuccess(amount);
        return;
      }

      onPreauthFailed(transactionResult);
    },
    onError: onUnknownError,
  });

  return {
    preauthorize,
    isLoading: isPending,
  };
};
